import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, DialogContent } from '@mui/material';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React, { FC, memo, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePermissionWrapper } from 'src/components/common/hooks/usePermissionWrapper';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { AppState } from 'src/store';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import EditMetadataModal from './EditMetadataModal';
import { EditMetaDataProps } from './types';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}));

const MetaDetail: FC<any> = ({
  expanded,
  accordionInfo,
  documentProps,
  fetchUpdatedData,
  handleToogleAccordion,
  latestVersion
}: EditMetaDataProps) => {
  const [open, setOpen] = React.useState(false);
  const [documentMeta, setDocumentMeta] = useState(documentProps);
  const documentDetail: any = useSelector((state: AppState) => state.documentDetail);
  const { preferedDateFormat } = usePreferedDateFormat();
  const { checkPermission } = usePermissionWrapper();
  const handleClickOpen = (event: any) => {
    setOpen(true);
    event.stopPropagation();
  };

  function createData(name: string, value: string | null) {
    return { name, value };
  }
  const dateString = documentDetail?.data[0]?.tmf_doc_date;
  const date = preferedDateFormat(dateString);

  const docVersion = documentDetail.data[0].tmf_doc_version_number;

  const rows = [
    createData(
      'Type:',
      documentDetail.data[0] &&
        (documentDetail.data[0].tmf_doc_type
          ? documentDetail.data[0].tmf_doc_type
          : documentDetail.data[0].tmf_doc_type)
    ),
    createData(
      'SubType:',
      documentDetail.data[0] &&
        (documentDetail.data[0].tmf_doc_subtype
          ? documentDetail.data[0].tmf_doc_subtype
          : documentDetail.data[0].tmf_doc_subtype)
    ),
    createData(
      'Artifact Number:',
      documentDetail.data[0] &&
        (documentDetail.data[0].tmf_artifact_number
          ? documentDetail.data[0].tmf_artifact_number
          : documentDetail.data[0].tmf_artifact_number)
    ),
    createData('Artifact Name:', documentDetail.data[0] && documentDetail.data[0].tmf_artifact_name)
  ];

  const contentRows = [
    createData(
      'Require Certified Copy?:',
      documentDetail.data[0].tmf_doc_viewer_req_certified_copy == 'Y' ? 'Yes' : 'No'
    ),
    createData('Document Number:', documentDetail.data[0] && documentDetail.data[0].tmf_document_id),
    createData('Created By:', documentDetail.data[0] && documentDetail.data[0].tmf_doc_created_by),
    createData(
      'Last Modified By:',
      documentDetail.data[0] && documentDetail.data[0].tmf_doc_last_modified_by
        ? documentDetail.data[0].tmf_doc_last_modified_by
        : documentDetail.data[0].tmf_doc_created_by
    ),
    createData(
      'Version Number:',
      parseFloat(documentDetail.data[0] && documentDetail.data[0].tmf_doc_version_number).toFixed(1)
    ),
    createData('Document Date:', date),
    createData('Status:', documentDetail.data[0] && documentDetail.data[0].tmf_doc_status),
    createData('Study:', documentDetail.data[0] && documentDetail.data[0].tmf_doc_study),
    createData('Filing level:', documentDetail.data[0] && documentDetail.data[0].tmf_doc_level),
    (documentDetail.data[0] && documentDetail.data[0].tmf_doc_level === 'Country') ||
    (documentDetail.data[0] && documentDetail.data[0].tmf_doc_level === 'Site')
      ? createData('Country:', documentDetail.data[0] && documentDetail.data[0].tmf_doc_country)
      : null,
    documentDetail.data[0] && documentDetail.data[0].tmf_doc_level === 'Site'
      ? createData('Site:', documentDetail.data[0] && documentDetail.data[0].tmf_doc_site)
      : null,
    createData(
      'Department:',
      documentDetail.data[0] && documentDetail.data[0].tmf_doc_viewer_department == 'None'
        ? ' '
        : documentDetail.data[0].tmf_doc_viewer_department
    ),
    createData('Language:', documentDetail.data[0] && documentDetail.data[0].tmf_doc_viewer_language),
    createData('Milestone:', documentDetail.data[0] && documentDetail.data[0].tmf_doc_milestone)
  ];

  return (
    <>
      <Accordion
        expanded={accordionInfo.position === expanded}
        onChange={handleToogleAccordion(accordionInfo.position)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
          <Typography sx={{ width: '60%', flexShrink: 0 }} className="acc-header">
            Details
            {checkPermission('update_document_details') && (
              <Button
                disabled={
                  documentDetail.data[0] && documentDetail.data[0].tmf_doc_latest_version
                    ? parseFloat(documentDetail.data[0] && documentDetail.data[0].tmf_doc_version_number)?.toFixed(
                        1
                      ) !==
                      parseFloat(documentDetail.data[0] && documentDetail.data[0].tmf_doc_latest_version)?.toFixed(1)
                    : docVersion < latestVersion
                }
                onClick={event => handleClickOpen(event)}
              >
                <EditIcon />
              </Button>
            )}
            {!checkPermission('update_document_details') && (
              <Button disabled={!checkPermission('update_document_details')}>
                <EditIcon />
              </Button>
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <Grid container className="header-row">
              <Grid md={6} container>
                <h4>Document Name:</h4>
              </Grid>
              <Grid md={6} className="word-wrap">
                {documentDetail.data[0] && documentDetail.data[0].tmf_document_name}
              </Grid>
            </Grid>
            <Grid container>
              <Grid md={6}>
                <h4>Description</h4>
              </Grid>
              <Grid md={6}></Grid>
            </Grid>

            <TableContainer component={Paper}>
              <Table aria-label="simple table">
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        {row.name}
                      </TableCell>
                      <TableCell align="right">{row.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Grid container>
              <Grid md={6}>
                <h4>Content</h4>
              </Grid>
              <Grid md={6}></Grid>
            </Grid>

            <TableContainer component={Paper}>
              <Table aria-label="simple table">
                <TableBody>
                  {contentRows.map(row => (
                    <TableRow key={row?.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        {row?.name}
                      </TableCell>
                      <TableCell align="right">{row?.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <PopupComponent aria-labelledby="customized-dialog-title" open={open} maxWidth="md">
        <DialogContent dividers>
          <EditMetadataModal
            actionType={''}
            expanded={expanded}
            documentProps={documentDetail.data[0]}
            setDocumentMeta={setDocumentMeta}
            setOpen={setOpen}
            fetchUpdatedData={fetchUpdatedData}
            accordionInfo={accordionInfo}
            handleToogleAccordion={handleToogleAccordion}
            latestVersion={latestVersion}
            status={documentDetail.data[0].status}
          />
        </DialogContent>
      </PopupComponent>
    </>
  );
};

export default memo(MetaDetail);
